import { useMemo, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

import Box from '../../organisms/note/Box'
import Button from 'components/atoms/Button'
import NoData from 'components/atoms/NoData'
import { Note, PayerNotes } from 'store/types'

const validation = Yup.object().shape({
  newNote: Yup.string().required('Required'),
})

interface IProps {
  userContext: 'LAB' | 'DME'
  payerNotes?: PayerNotes
  type?: 'scheduling' | 'labOnly'
  notes?: Note[]
  addNote: (n: string, type?: string) => Promise<void>
  pinNote?: (param: { uuid: string; pinned: boolean }) => Promise<void>
  deleteNote?: (param: { uuid: string }) => Promise<void>
  updateNote?: (param: { uuid: string; text: string }) => Promise<void>
}

const PhysicianNotes = ({
  userContext,
  type,
  notes,
  addNote,
  pinNote,
  deleteNote,
  updateNote,
  payerNotes,
}: IProps) => {
  const [editingUuid, setEditingUuid] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')

  const sortedNotes = useMemo(() => {
    if (!notes) return []
    return [...notes]
      ?.filter(
        (note) =>
          userContext === 'DME' ||
          (type === 'scheduling' ? note.type === 'scheduling' : note.type !== 'scheduling'),
      )
      .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
      .sort((a, b) => (a.pinned && !b.pinned ? -1 : 1))
  }, [notes, type])

  const startEditing = (note: Note) => {
    setEditingUuid(note.uuid)
    setEditingText(note.text || '')
  }

  const stopEditing = () => {
    setEditingUuid(null)
    setEditingText('')
  }

  return (
    <>
      <div className="col-sm-6 mb-2">
        <div
          className="note_panel pt-3 h-100"
          style={{ borderTop: '1px solid #dee2e6', maxHeight: '250px' }}>
          {notes?.length || payerNotes?.notes ? (
            sortedNotes.map((note, i) => (
              <div key={i}>
                <Box
                  pinNote={pinNote}
                  deleteNote={deleteNote}
                  editNote={startEditing}
                  isEditable={true}
                  userContext={userContext}
                  note={note}
                  orderId=""
                />
                {editingUuid === note.uuid && (
                  <div className="px-3 pb-3">
                    <textarea
                      className="form-control"
                      rows={4}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                    />
                    <div className="text-right mt-2">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm mr-2"
                        onClick={stopEditing}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={!editingText.trim()}
                        onClick={async () => {
                          if (!updateNote) return
                          await updateNote({ uuid: note.uuid, text: editingText })
                          stopEditing()
                        }}>
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <NoData
              text={`No ${type === 'scheduling' ? 'Scheduling' : ''} Notes`}
              className="msg_noData font-italic py-3 my-2"
            />
          )}
        </div>
      </div>
      {(userContext !== 'DME' || type !== 'scheduling') && (
        <Formik
          initialValues={{ newNote: '' }}
          validationSchema={validation}
          enableReinitialize={true}
          onSubmit={async ({ newNote }, { resetForm }) => {
            await addNote(newNote, type)
            resetForm()
          }}>
          {({ touched, errors, isSubmitting }) => (
            <Form className="col-sm-6">
              <div className="row mb-2 mr-1 ml-1">
                <div className="col form-group pt-1">
                  <label htmlFor="note">Add a note</label>
                  <Field
                    component="textarea"
                    className="form-control"
                    name="newNote"
                    id="newNote"
                    rows="4"
                    placeholder="Enter your note..."
                  />
                  {touched.newNote && errors.newNote && (
                    <div className="text-danger">{errors.newNote}</div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col text-right mr-2">
                  <Button id="btn-ts" label="Save Note" type="submit" disabled={isSubmitting} />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  )
}

export default PhysicianNotes
